import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@grassrootly/rn-core";
import useBaseRequest from "../../../../api/BaseRequest";
import { groupMatchesActions } from "../../../../api/groups/GroupMatchesReducer";
import { GetGroupMatches, GroupMatchesParams } from "@grassrootly/api/providers/supabase-provider/actions";
import moment from "moment";
import { GroupTabItemProps } from "../GroupTabs.types";
import { IGroupMatches } from "@grassrootly/api/entities/IGroupsMatches";

export interface IGroupMatchesHook {
  loading: boolean;
  groupMatches: IGroupMatches[] | null;
  loadMoreMatches: () => void;
  loadingMore: boolean;
  hasMoreMatches: boolean;
}

export function useGroupMacthes({ group }: GroupTabItemProps): IGroupMatchesHook {
  const dispatch = useAppDispatch();
  const groupId = group.id;
  const playerId = useAppSelector((state) => state.auth.user.id);
  //Show games up to 1 month in future in the Matches tab.
  const dateTo = moment().add(1, "month").format("YYYY-MM-DDTHH:mm:ss");
  
  const [matchesOffset, setMatchesOffset] = useState<number>(0);
  const [hasMoreMatches, setHasMoreMatches] = useState<boolean>(true);
  const [allMatches, setAllMatches] = useState<IGroupMatches[]>([]);
  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const {
    loading,
    execute: getMatchesRequest,
  } = useBaseRequest(GetGroupMatches, {
    onCompleted: (data) => {
      if (!data) return;
      
      // Check if this is a new group or initial load
      if (isInitialLoad || currentGroupId !== groupId) {
        // First load or new group - replace all matches
        setAllMatches(data);
        setCurrentGroupId(groupId);
        setMatchesOffset(data.length); // Set offset to number of items loaded
        setIsInitialLoad(false);
      } else {
        // Pagination load - append matches
        setAllMatches(prevMatches => [...prevMatches, ...data]);
        setMatchesOffset(prev => prev + data.length); // Increment offset by items loaded
      }
      
      setHasMoreMatches(data.length === 10); // If we got less than 10, no more matches
    },
  });

  // Use the same request handler for both initial load and pagination
  const getMatches = getMatchesRequest;
  const loadingMore = loading;

  const handleLoadMoreMatches = () => {
    if (hasMoreMatches && !loadingMore && groupId) {
      setIsInitialLoad(false); // Ensure this is treated as pagination
      getMatches({ 
        date_to: dateTo, 
        grid: groupId, 
        pid: playerId,
        limit: 10, 
        offset: matchesOffset 
      });
    }
  };

  useEffect(() => {
    if (groupId && groupId !== currentGroupId) {
      // Reset pagination and load first page when group changes
      setMatchesOffset(0);
      setHasMoreMatches(true);
      setAllMatches([]);
      setCurrentGroupId(groupId);
      setIsInitialLoad(true);
      getMatches({ date_to: dateTo, grid: groupId, pid: playerId, limit: 10, offset: 0 });
    }
  }, [groupId, currentGroupId]);

  return {
    loading,
    groupMatches: allMatches.length > 0 ? allMatches : null,
    loadMoreMatches: handleLoadMoreMatches,
    loadingMore,
    hasMoreMatches,
  };
}
